const BINARY_TAG = 111222333;
const BINARY_VER = 0;
export var NodeType;
(function (NodeType) {
    NodeType[NodeType["NULL"] = 0] = "NULL";
    NodeType[NodeType["BOOLEAN"] = 1] = "BOOLEAN";
    NodeType[NodeType["INTEGER"] = 2] = "INTEGER";
    NodeType[NodeType["REAL"] = 3] = "REAL";
    NodeType[NodeType["STRING"] = 4] = "STRING";
    NodeType[NodeType["DATETIME"] = 5] = "DATETIME";
    NodeType[NodeType["ANY"] = 6] = "ANY";
    NodeType[NodeType["ARRAY"] = 7] = "ARRAY";
    NodeType[NodeType["DICT"] = 8] = "DICT";
    NodeType[NodeType["NONE"] = 9] = "NONE";
})(NodeType || (NodeType = {}));
class IDataStream {
    constructor(data) {
        if (!data) {
            throw new Error('Null binary data!');
        }
        if (!(data instanceof Uint8Array)) {
            throw new Error('data must be Uint8Array');
        }
        this.data = data;
        this.offset = 0;
    }
    readUInt8() {
        if (this.data.length < this.offset + 1) {
            throw new Error('Not enough data to read byte!');
        }
        const val = this.data[this.offset];
        this.offset += 1;
        return val;
    }
    readBool() {
        return this.readUInt8() !== 0;
    }
    readUInt32() {
        if (this.data.length < this.offset + 4) {
            throw new Error('Not enough data to read uint!');
        }
        const val = new DataView(this.data.buffer, this.data.byteOffset + this.offset, 4).getUint32(0, true);
        this.offset += 4;
        return val;
    }
    readInt32() {
        if (this.data.length < this.offset + 4) {
            throw new Error('Not enough data to read int!');
        }
        const val = new DataView(this.data.buffer, this.data.byteOffset + this.offset, 4).getInt32(0, true);
        this.offset += 4;
        return val;
    }
    readDouble() {
        if (this.data.length < this.offset + 8) {
            throw new Error('Not enough data to read double!');
        }
        const val = new DataView(this.data.buffer, this.data.byteOffset + this.offset, 8).getFloat64(0, true);
        this.offset += 8;
        return val;
    }
    readString() {
        const strSize = this.readUInt32();
        if (this.data.length < this.offset + strSize) {
            throw new Error(`Not enough data to read string of size [${strSize}]!`);
        }
        const str = new TextDecoder().decode(this.data.subarray(this.offset, this.offset + strSize));
        this.offset += strSize;
        return str;
    }
    readDateTime() {
        return new Date(this.readString());
    }
    readBinary() {
        const binSize = this.readUInt32();
        if (this.data.length < this.offset + binSize) {
            throw new Error(`Not enough data to read binary of size [${binSize}]!`);
        }
        const bin = this.data.slice(this.offset, this.offset + binSize);
        this.offset += binSize;
        return bin;
    }
}
class ODataStream {
    constructor() {
        this.buffer = new Uint8Array(1024);
        this.offset = 0;
    }
    writeUInt8(val) {
        this.provision(1);
        this.buffer[this.offset] = val;
        this.offset += 1;
    }
    writeBool(val) {
        this.writeUInt8(val ? 1 : 0);
    }
    writeUInt32(val) {
        this.provision(4);
        new DataView(this.buffer.buffer).setUint32(this.offset, val, true);
        this.offset += 4;
    }
    writeInt32(val) {
        this.provision(4);
        new DataView(this.buffer.buffer).setInt32(this.offset, val, true);
        this.offset += 4;
    }
    writeDouble(val) {
        this.provision(8);
        new DataView(this.buffer.buffer).setFloat64(this.offset, val, true);
        this.offset += 8;
    }
    writeString(str) {
        const encodedStr = new TextEncoder().encode(str);
        this.writeUInt32(encodedStr.length);
        this.provision(encodedStr.length);
        this.buffer.set(encodedStr, this.offset);
        this.offset += encodedStr.length;
    }
    writeDateTime(date) {
        this.writeString(date.toISOString());
    }
    writeBinary(bin) {
        this.writeUInt32(bin.length);
        this.provision(bin.length);
        this.buffer.set(bin, this.offset);
        this.offset += bin.length;
    }
    provision(count) {
        const targetSize = this.offset + count;
        if (this.buffer.length < targetSize) {
            this.resizeBuffer(targetSize);
        }
    }
    finalizeBuffer() {
        const result = this.buffer.slice(0, this.offset);
        return result;
    }
    resizeBuffer(size) {
        const newBuff = new Uint8Array(size);
        newBuff.set(this.buffer);
        this.buffer = newBuff;
    }
}
function readMsgNode(stream, version) {
    const type = stream.readUInt8();
    switch (type) {
        case NodeType.NULL:
            return null;
        case NodeType.BOOLEAN:
            return stream.readBool();
        case NodeType.INTEGER:
            return stream.readInt32();
        case NodeType.REAL:
            return stream.readDouble();
        case NodeType.STRING:
            return stream.readString();
        case NodeType.DATETIME:
            return stream.readDateTime();
        case NodeType.ANY:
            return stream.readBinary();
        case NodeType.ARRAY: {
            const arr = [];
            const count = stream.readUInt32();
            for (let i = 0; i < count; ++i) {
                arr.push(readMsgNode(stream, version));
            }
            return arr;
        }
        case NodeType.DICT: {
            const obj = {};
            const count = stream.readUInt32();
            for (let i = 0; i < count; ++i) {
                const name = stream.readString();
                obj[name] = readMsgNode(stream, version);
            }
            return obj;
        }
        case NodeType.NONE:
            return undefined;
        default:
            throw new Error(`Unknown node type [${type}]`);
    }
}
function getType(val) {
    if (val === undefined)
        return NodeType.NONE;
    if (val === null)
        return NodeType.NULL;
    switch (typeof val) {
        case 'boolean':
            return NodeType.BOOLEAN;
        case 'number':
            return Number.isInteger(val) ? NodeType.INTEGER : NodeType.REAL;
        case 'string':
            return NodeType.STRING;
        case 'object':
            if (val instanceof Uint8Array /* || val instanceof ArrayBuffer*/)
                return NodeType.ANY;
            if (Array.isArray(val))
                return NodeType.ARRAY;
            if (val instanceof Date)
                return NodeType.DATETIME;
            return NodeType.DICT;
        default:
            throw new Error(`Unknown value type [${val}]`);
    }
}
function writeMsgNode(stream, node) {
    let type = getType(node);
    if (type === NodeType.NONE) {
        type = NodeType.NULL;
    }
    stream.writeUInt8(type);
    switch (type) {
        case NodeType.NULL:
            break;
        case NodeType.BOOLEAN:
            stream.writeBool(node);
            break;
        case NodeType.INTEGER:
            stream.writeInt32(node);
            break;
        case NodeType.REAL:
            stream.writeDouble(node);
            break;
        case NodeType.STRING:
            stream.writeString(node.toString());
            break;
        case NodeType.DATETIME:
            stream.writeDateTime(node);
            break;
        case NodeType.ANY:
            stream.writeBinary(node);
            break;
        case NodeType.ARRAY:
            stream.writeUInt32(node.length);
            node.forEach((val) => writeMsgNode(stream, val));
            break;
        case NodeType.DICT: {
            const entries = Object.entries(node);
            stream.writeUInt32(entries.length);
            for (const [key, value] of entries) {
                stream.writeString(key);
                writeMsgNode(stream, value);
            }
            break;
        }
        default:
            throw new Error(`Unknown node type [${type}]`);
    }
}
export function decode(data) {
    try {
        const stream = new IDataStream(data);
        const tag = stream.readUInt32();
        if (tag !== BINARY_TAG) {
            throw new Error(`Unrecognized binary tag [${tag}]!`);
        }
        const ver = stream.readUInt32();
        if (ver > BINARY_VER) {
            throw new Error(`Unsupported binary version [${ver}]!`);
        }
        return readMsgNode(stream, ver);
    }
    catch (e) {
        console.error(`Failed to decode binary message. Error: ${e.message}`);
        return null;
    }
}
export function encode(msg) {
    try {
        const stream = new ODataStream();
        stream.writeUInt32(BINARY_TAG);
        stream.writeUInt32(BINARY_VER);
        writeMsgNode(stream, msg);
        return stream.finalizeBuffer();
    }
    catch (e) {
        console.error(`Failed to encode binary message. Error: ${e.message}`);
        return null;
    }
}
//# sourceMappingURL=BinaryMessage.js.map